import { motion } from 'framer-motion'
import { Building2, Zap, Leaf, Fuel, Truck, TrendingUp, Recycle, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SEO from './SEO'
import '../styles/glassmorphism.css'
import backgroundImage from '../assets/AllServices/background.avif'
import constructionImage from '../assets/AllServices/construction.jpg'
import powerGenerationImage from '../assets/AllServices/power_generation.jpg'
import environmentalImage from '../assets/AllServices/Environmental.jpg'
import oilImage from '../assets/AllServices/oil.jpg'
import logisticsImage from '../assets/AllServices/logistics.jpg'
import generalTradingImage from '../assets/AllServices/general_trading.jpg'
import recyclingImage from '../assets/AllServices/recycling.jpg'



const AllServices = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const services = [
    {
      id: 1,
      title: t('header.servicesList.construction'),
      description: t('services.serviceDescriptions.construction'),
      icon: Building2,
      route: '/services/construction',
      image: constructionImage
    },
    {
      id: 2,
      title: t('header.servicesList.powerGeneration'),
      description: t('services.serviceDescriptions.powerGeneration'),
      icon: Zap,
      route: '/services/power-generation',
      image: powerGenerationImage
    },
    {
      id: 3,
      title: t('header.servicesList.environmental'),
      description: t('services.serviceDescriptions.environmental'),
      icon: Leaf,
      route: '/services/environmental',
      image: environmentalImage
    },
    {
      id: 4,
      title: t('header.servicesList.oilFuelSupply'),
      description: t('services.serviceDescriptions.oilFuelSupply'),
      icon: Fuel,
      route: '/services/oil-fuel-supply',
      image: oilImage
    },
    {
      id: 5,
      title: t('header.servicesList.logistics'),
      description: t('services.serviceDescriptions.logistics'),
      icon: Truck,
      route: '/services/logistics',
      image: logisticsImage
    },
    {
      id: 6,
      title: t('header.servicesList.generalTrading'),
      description: t('services.serviceDescriptions.generalTrading'),
      icon: TrendingUp,
      route: '/services/general-trading',
      image: generalTradingImage
    },
    {
      id: 7,
      title: t('header.servicesList.recycling'),
      description: t('services.serviceDescriptions.recycling'),
      icon: Recycle,
      route: '/services/recycling',
      image: recyclingImage
    }
  ]

  const getCardSpan = (index) => {
    // Responsive pattern for 7 items across all screen sizes
    const patterns = [
      'col-span-1 sm:col-span-2 lg:col-span-2 xl:col-span-2 row-span-1 sm:row-span-2 lg:row-span-2', // 0: Construction
      'col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1 row-span-1 sm:row-span-2 lg:row-span-2', // 1: Power
      'col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1 row-span-1 sm:row-span-2 lg:row-span-2', // 2: Environmental
      'col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1 row-span-1 sm:row-span-2 lg:row-span-2', // 3: Oil
      'col-span-1 sm:col-span-2 lg:col-span-2 xl:col-span-2 row-span-1 sm:row-span-2 lg:row-span-2', // 4: Logistics
      'col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1 row-span-1 sm:row-span-2 lg:row-span-2', // 5: General Trading
      'col-span-1 sm:col-span-2 lg:col-span-2 xl:col-span-2 lg:col-start-2 xl:col-start-2 row-span-1 sm:row-span-2 lg:row-span-2', // 6: Recycling
    ]
    return patterns[index] || 'col-span-1 row-span-1'
  }

  return (
    <div className="min-h-screen relative bg-black text-white">
      <SEO
        title="Our Services | Construction, Logistics & Environmental Solutions"
        description="Explore AL-KONDA Group's comprehensive services: construction, power generation, environmental services, oil & fuel supply, logistics, general trading, and recycling solutions."
        keywords="construction services, power generation, environmental services, logistics, oil fuel supply, general trading, recycling, Iraq services"
        url="/services"
      />
      {/* Background Elements */}
      <div className="fixed inset-0 z-0">
        <img
          src={backgroundImage}
          alt="Background"
          className="w-full h-full object-cover opacity-40 blur-md"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black/90" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
      </div>

      <div className="relative z-10 container mx-auto px-3 xs:px-4 sm:px-6 lg:px-8 xl:px-12 pt-32 xs:pt-36 sm:pt-40 lg:pt-44 pb-16 xs:pb-20 sm:pb-24 lg:pb-32">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12 xs:mb-16 sm:mb-20 lg:mb-24"
        >
          <div className="inline-block px-3 xs:px-4 py-1 xs:py-1.5 mb-4 xs:mb-6 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm">
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-white/80">{t('services.title')}</span>
          </div>
          <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light mb-4 xs:mb-6 tracking-tight leading-tight">
            {t('services.subtitle')} <br className="hidden xs:block" />
            <span className="font-medium text-transparent bg-clip-text bg-gradient-to-r from-white via-white/80 to-white/50">{t('services.subtitleHighlight')}</span>
          </h1>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 xs:grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-3 xs:gap-4 sm:gap-5 lg:gap-6 auto-rows-[220px] xs:auto-rows-[240px] sm:auto-rows-[260px] lg:auto-rows-[280px] xl:auto-rows-[280px]">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => navigate(service.route)}
              className={`group relative cursor-pointer overflow-hidden rounded-xl xs:rounded-2xl sm:rounded-[2rem] border border-white/10 shadow-2xl ${getCardSpan(index)}`}
            >
              {/* Image Background */}
              <div className="absolute inset-0">
                <img
                  src={service.image}
                  alt={service.title}
                  loading="lazy"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out will-change-transform"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-70 transition-opacity duration-300" />
              </div>

              {/* Half-Blur Effect */}
              <div
                className="absolute inset-x-0 bottom-0 h-[65%] backdrop-blur-[10px] bg-black/10 transition-all duration-300 group-hover:backdrop-blur-[14px] group-hover:bg-black/20"
                style={{
                  maskImage: 'linear-gradient(to top, black 0%, black 30%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to top, black 0%, black 30%, transparent 100%)'
                }}
              />

              {/* Content Overlay */}
              <div className="absolute inset-0 p-4 xs:p-5 sm:p-6 lg:p-8 flex flex-col justify-end">
                <div className="transform translate-y-1 group-hover:translate-y-0 transition-transform duration-300 ease-out">
                  {/* Icon & Label */}
                  <div className="flex items-center gap-2 xs:gap-3 mb-3 xs:mb-4 opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-8 h-8 xs:w-9 xs:h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 group-hover:bg-white/20 group-hover:border-white/40 transition-all duration-300">
                      <service.icon className="w-4 h-4 xs:w-4.5 xs:h-4.5 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div className="h-px w-6 xs:w-8 bg-white/30 group-hover:w-8 xs:group-hover:w-12 transition-all duration-300" />
                    <span className="text-[9px] xs:text-[10px] font-bold tracking-[0.2em] uppercase text-white/90">{t('services.explore')}</span>
                  </div>

                  <h2 className="text-lg xs:text-xl sm:text-2xl lg:text-3xl font-medium text-white mb-2 xs:mb-3 leading-tight drop-shadow-lg group-hover:text-white transition-colors duration-300">
                    {service.title}
                  </h2>

                  <p className="text-white/70 text-xs xs:text-sm sm:text-base leading-relaxed line-clamp-2 xs:line-clamp-3 sm:line-clamp-2 group-hover:text-white/90 transition-colors duration-300 max-w-xs xs:max-w-sm sm:max-w-md drop-shadow-md font-light">
                    {service.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default AllServices
